"use client";

import { useEffect } from "react";
import { ACTIVITIES } from "@/lib/game/activities";
import { DIFFICULTY_LABELS } from "@/lib/game/difficulty";
import { ACTIVITY_ICON_IDS } from "@/lib/game/uiIcons";
import { completedDifficultyCount } from "@/lib/game/progress";
import { DifficultyPicker } from "../activities/DifficultyPicker";
import { GameMenuTile, GamePanel, GameScreenHeader } from "../game-ui";
import { MenuScreenShell } from "../MenuScreenShell";
import { useGame } from "../GameProvider";

const ACTIVITY_TONES = ["violet", "sky", "amber", "rose"] as const;

export function ActivitiesScreen() {
  const { goTo, progress, setDifficulty, trackActivitiesMenuVisit } = useGame();

  useEffect(() => {
    trackActivitiesMenuVisit();
  }, [trackActivitiesMenuVisit]);

  return (
    <MenuScreenShell variant="menu">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 py-6">
        <GameScreenHeader
          title="Play"
          subtitle="Win stars every time you finish a game!"
          onBack={() => goTo("home")}
        />

        <GamePanel tone="play" className="mb-5 shrink-0">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-violet-100/80">
            Difficulty for all activities
          </p>
          <DifficultyPicker value={progress.difficulty} onChange={setDifficulty} />
          <p className="mt-2 text-xs text-violet-100/70">
            Replay as much as you like — try harder tiers for more stars!
          </p>
        </GamePanel>

        <nav
          className="mx-auto grid w-full max-w-md grid-cols-2 gap-3 pb-6"
          aria-label="Play"
        >
          {ACTIVITIES.map((activity, index) => {
            const doneCount = completedDifficultyCount(progress, activity.id);
            const allDone = doneCount >= 3;
            const iconId = ACTIVITY_ICON_IDS[activity.id] ?? "play";
            const badge = allDone
              ? "Done!"
              : doneCount > 0
                ? `${doneCount}/3`
                : undefined;
            const subtitle =
              doneCount > 0 && !allDone
                ? `Try ${DIFFICULTY_LABELS[progress.difficulty]} for more!`
                : activity.subtitle;

            return (
              <GameMenuTile
                key={activity.id}
                icon={iconId}
                label={activity.title}
                subtitle={subtitle}
                tone={ACTIVITY_TONES[index % ACTIVITY_TONES.length]}
                badge={badge}
                onClick={() => goTo(activity.screen)}
              />
            );
          })}
        </nav>
      </div>
    </MenuScreenShell>
  );
}
